import {
  Condition,
  FieldGetter,
  isRule,
  MatchValue,
  Operator,
  Rule
} from '@dadohr/shared/domainTypes/rules';
import { assertNever } from 'assert-never';
import { Timestamp } from '../time';

const isBlank = (x: any): boolean =>
  !!(x === null || x === undefined || x === '');

const applyOperator = (
  op: Operator,
  expected: MatchValue,
  actual: MatchValue
) => {
  if (
    op === 'isAnyOf' ||
    op === 'isNoneOf' ||
    op === 'containsAnyOf' ||
    op === 'doesNotContainAnyOf'
  ) {
    console.warn(
      'Expanded string operator reached applyOperator - this should not happen:',
      op
    );
    return false;
  }

  if (actual === undefined || actual === null) {
    if (op === '!=' || op === '!~' || op === 'isBlank') {
      return true;
    }
  }

  if (op === 'isBlank') {
    return isBlank(actual);
  }
  if (op === 'isNotBlank') {
    return !isBlank(actual);
  }

  if (typeof expected !== typeof actual) {
    // console.warn('type mismatch', expected, actual);
    return false;
  }
  if (op === '=~' || op === '!~') {
    // =~ / !~ surface in the rule-builder UI as "contains" / "does not
    // contain" — substring semantics. Use String.prototype.includes
    // directly. The previous implementation built `new RegExp(expected)`
    // which silently interpreted regex metacharacters (parens, dots,
    // pipes, +, *, etc.) — so a value like "Manager (Direct)" was read
    // as the regex /Manager (Direct)/ which matches "Manager Direct"
    // (capture group), not the literal string "Manager (Direct)".
    // Mirrors the v2 fix at packages/shared/src/services/rules/index.ts
    // (commit f09bf92e57). This local copy was missed because the FE
    // doesn't import the shared matcher — it has its own duplicate here.
    if (typeof actual === 'string' && typeof expected === 'string') {
      if (op === '=~') return actual.includes(expected);
      if (op === '!~') return !actual.includes(expected);
      return assertNever(op);
    } else {
      console.warn('operator mismatch', op, expected, actual);
      return false;
    }
  }
  if (typeof expected === 'boolean' && typeof actual === 'boolean') {
    if (op !== '==' && op !== '!=') {
      console.warn('operator mismatch', op, expected, actual);
      return false;
    }
  }

  if (op === '==') {
    return actual === expected;
  }

  if (op === '!=') {
    return actual !== expected;
  }
  if (op === '<') {
    return actual < expected;
  }
  if (op === '<=') {
    return actual <= expected;
  }
  if (op === '>') {
    return actual > expected;
  }
  if (op === '>=') {
    return actual >= expected;
  }
  return assertNever(op);
};

const matchCondition = <Field extends string, T>(
  cond: Condition<Field>,
  getter: FieldGetter<Field, T>,
  t: T,
  referenceDate: Timestamp
) => {
  const { op, k: key, v: expected } = cond;
  const actual = getter(key, t, referenceDate);
  return applyOperator(op, expected, actual);
};

export const matchRule = <Field extends string, T>(
  rule: Rule<Field>,
  getter: FieldGetter<Field, T>,
  entity: T,
  referenceDate: Timestamp
): boolean => {
  for (const el of rule.els) {
    const matches = isRule(el)
      ? matchRule(el, getter, entity, referenceDate)
      : matchCondition(el, getter, entity, referenceDate);
    if (rule.type === 'all' && !matches) {
      return false;
    }
    if (rule.type === 'any' && matches) {
      return true;
    }
  }
  return rule.type === 'all';
};

export const filterByRule = <Field extends string, T>(
  rule: Rule<Field>,
  getter: FieldGetter<Field, T>,
  entities: T[],
  referenceDate: Timestamp
): T[] => entities.filter((e) => matchRule(rule, getter, e, referenceDate));

export function extractUniqueKValues(filterObj: Rule<string> | null): string[] {
  if (!filterObj) return [];

  const kValues = new Set<string>();

  function traverse(obj: Rule<string>): void {
    if (!isRule(obj)) {
      // If it's a condition object with k, op, v, add the k value
      kValues.add((obj as any).k);
    } else if (isRule(obj)) {
      // If it's a group object with type and els, traverse the els array
      obj.els.forEach((el) => traverse(el as any));
    }
  }

  traverse(filterObj);
  return Array.from(kValues);
}

/**
 * All the following functions are helpers to surface what specific rule failed
 */

export interface ConditionFailure<Field extends string> {
  condition: Condition<Field>;
  actualValue: MatchValue;
  expectedValue: MatchValue;
  operator: Operator;
  field: Field;
}

export interface RuleEvaluationResult<Field extends string> {
  matches: boolean;
  failedConditions: ConditionFailure<Field>[];
}

const applyOperatorWithDetails = <Field extends string>(
  op: Operator,
  expected: MatchValue,
  actual: MatchValue,
  condition: Condition<Field>
): { matches: boolean; failure?: ConditionFailure<Field> } => {
  const isBlank = (x: any): boolean =>
    !!(x === null || x === undefined || x === '');

  if (
    op === 'isAnyOf' ||
    op === 'isNoneOf' ||
    op === 'containsAnyOf' ||
    op === 'doesNotContainAnyOf'
  ) {
    console.warn(
      'Expanded string operator reached applyOperator - this should not happen:',
      op
    );
    return { matches: false };
  }

  let matches = false;

  if (actual === undefined || actual === null) {
    if (op === '!=' || op === '!~' || op === 'isBlank') {
      matches = true;
    }
  } else if (op === 'isBlank') {
    matches = isBlank(actual);
  } else if (op === 'isNotBlank') {
    matches = !isBlank(actual);
  } else if (typeof expected !== typeof actual) {
    matches = false;
  } else if (op === '=~' || op === '!~') {
    // Substring semantics — see applyOperator's =~/!~ branch above for the
    // full rationale (parens-in-value regex-metachar bug). Mirrors the v2
    // fix at packages/shared/src/services/rules/index.ts (commit f09bf92e57).
    if (typeof actual === 'string' && typeof expected === 'string') {
      if (op === '=~') {
        matches = actual.includes(expected);
      } else if (op === '!~') {
        matches = !actual.includes(expected);
      }
    } else {
      console.warn('operator mismatch', op, expected, actual);
      matches = false;
    }
  } else if (typeof expected === 'boolean' && typeof actual === 'boolean') {
    if (op !== '==' && op !== '!=') {
      console.warn('operator mismatch', op, expected, actual);
      matches = false;
    } else {
      matches = op === '==' ? actual === expected : actual !== expected;
    }
  } else {
    switch (op) {
      case '==':
        matches = actual === expected;
        break;
      case '!=':
        matches = actual !== expected;
        break;
      case '<':
        matches = actual < expected;
        break;
      case '<=':
        matches = actual <= expected;
        break;
      case '>':
        matches = actual > expected;
        break;
      case '>=':
        matches = actual >= expected;
        break;
      default:
        matches = false;
    }
  }

  if (!matches) {
    return {
      matches: false,
      failure: {
        condition,
        actualValue: actual,
        expectedValue: expected,
        operator: op,
        field: condition.k
      }
    };
  }

  return { matches: true };
};

const matchConditionWithDetails = <Field extends string, T>(
  cond: Condition<Field>,
  getter: FieldGetter<Field, T>,
  t: T,
  referenceDate: Timestamp
): { matches: boolean; failure?: ConditionFailure<Field> } => {
  const { op, k: key, v: expected } = cond;
  const actual = getter(key, t, referenceDate);
  return applyOperatorWithDetails(op, expected, actual, cond);
};

export const matchRuleWithFailures = <Field extends string, T>(
  rule: Rule<Field>,
  getter: FieldGetter<Field, T>,
  entity: T,
  referenceDate: Timestamp
): RuleEvaluationResult<Field> => {
  const failedConditions: ConditionFailure<Field>[] = [];
  let hasAnyMatch = false;

  for (const el of rule.els) {
    if (isRule(el)) {
      const nestedResult = matchRuleWithFailures(
        el,
        getter,
        entity,
        referenceDate
      );

      if (!nestedResult.matches) {
        // Add all failed conditions from nested rules
        failedConditions.push(...nestedResult.failedConditions);

        if (rule.type === 'all') {
          // For 'all' rules, if any nested rule fails, the whole rule fails
          return {
            matches: false,
            failedConditions
          };
        }
      } else {
        hasAnyMatch = true;
        if (rule.type === 'any') {
          // For 'any' rules, if any nested rule passes, the whole rule passes
          return {
            matches: true,
            failedConditions: []
          };
        }
      }
    } else {
      const conditionResult = matchConditionWithDetails(
        el,
        getter,
        entity,
        referenceDate
      );

      if (!conditionResult.matches && conditionResult.failure) {
        failedConditions.push(conditionResult.failure);

        if (rule.type === 'all') {
          // For 'all' rules, if any condition fails, the whole rule fails
          return {
            matches: false,
            failedConditions
          };
        }
      } else if (conditionResult.matches) {
        hasAnyMatch = true;
        if (rule.type === 'any') {
          // For 'any' rules, if any condition passes, the whole rule passes
          return {
            matches: true,
            failedConditions: []
          };
        }
      }
    }
  }

  // Determine final result based on rule type
  if (rule.type === 'all') {
    return {
      matches: true,
      failedConditions: []
    };
  } else {
    return {
      matches: hasAnyMatch,
      failedConditions: hasAnyMatch ? [] : failedConditions
    };
  }
};
